import { BaseChatPackage } from '../utils'
import { useCallback, useEffect, useRef } from 'react'
import type { ChatChannelInstance } from '../types'

export type SyncChannelMembersOptions = { enabled?: boolean }
export class UseSyncChannelMembers extends BaseChatPackage {
  useSyncChannelMembers(
    channel: ChatChannelInstance | null,
    options?: SyncChannelMembersOptions
  ) {
    const { enabled = true } = options || {}
    const pendingInvalidateRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
      return () => {
        if (pendingInvalidateRef.current !== null) {
          clearTimeout(pendingInvalidateRef.current)
          pendingInvalidateRef.current = null
        }
      }
    }, [channel?.id, enabled])

    const handleMemberChange = useCallback(async (event: any) => {
      const eventChannelId = event.channel_id ||
        (event.cid ? event.cid.split(':')[1] : null) ||
        (event.channel?.id)

      if (eventChannelId && eventChannelId !== channel?.id) return

      const channelId = channel?.id
      if (!channelId) return

      const eventType = event?.type

      const addEvents = ['member.added', 'notification.added_to_channel']
      const deleteEvents = ['member.removed', 'notification.removed_from_channel']
      const updateEvents = ['member.updated', 'user.updated']

      const member = event?.member

      if (!member) return

      if (addEvents.includes(eventType)) {
        const count = this.queries?.members.manager.getItemCount({ channelId }) || 0

        if (count === this.queries?.members.LIMIT) {
          return this.queries.members.invalidateMembers(channelId)
        }

        if (count > this.queries?.members.LIMIT) {
          return
        }

        const memberId = member.user_id as string
        const cachedList = this.queries?.members.manager.getList({ channelId })
        const alreadyInList = cachedList?.pages?.some((page) =>
          page?.results?.some((row) => row.id === memberId),
        )
        if (alreadyInList) {
          return
        }

        await this.queries?.members.manager.addItem({ item: { ...member, id: memberId }, to: 'end' })

        if (pendingInvalidateRef.current !== null) {
          clearTimeout(pendingInvalidateRef.current)
        }
        pendingInvalidateRef.current = setTimeout(() => {
          pendingInvalidateRef.current = null
          this.queries?.members.invalidateMembers(channelId)
        }, 200)
      }
      if (deleteEvents.includes(eventType)) {
        this.queries?.members.manager.removeItem(member?.user_id)
      }

      if (updateEvents.includes(eventType)) {
        const isModerator = member?.is_moderator
        // updateItem returns null when itemMap doesn't have this member yet
        // (e.g. the member was just added but transformData hasn't run yet).
        // Fall back to a full refetch so the list reflects the backend state.
        const updated = await this.queries?.members.manager.updateItem(member?.user_id, { ...member, is_moderator: isModerator })
        if (updated === null) {
          this.queries?.members.invalidateMembers(channelId)
        }
      }
    }, [channel])

    useEffect(() => {
      if (!channel || !enabled) return

      const channelSubs = [
        channel.on('member.added', handleMemberChange),
        channel.on('member.removed', handleMemberChange),
        channel.on('member.updated', handleMemberChange),
      ]

      const client = (channel as any).getClient?.() || (channel as any).client
      let clientSubs: any[] = []

      if (client) {
        clientSubs = [
          client.on('notification.added_to_channel', handleMemberChange),
          client.on('notification.removed_from_channel', handleMemberChange),
          client.on('user.updated', handleMemberChange),
        ]
      }

      return () => {
        channelSubs.forEach(s => s?.unsubscribe?.())
        clientSubs.forEach(s => s?.unsubscribe?.())
      }
    }, [channel, enabled, channel?.id])
  }
}


